<template>
  <div class="d-flex align-items-center p-4 rounded-sm mb-3" :class="alertClasses" v-if="isVisible">
    <div class="d-flex align-items-center flex-grow-1">
      <CxIcon :icon="icon" :color="props.variant" class="mr-2" />

      <div class="ml-2" :class="`text-${props.variant}`">
        <CxTitle v-if="hasTitleSlot" level="4" fontWeight="semiBold" :class="textClasses">
          <slot name="title"></slot>
        </CxTitle>

        <CxText paragraph v-if="hasBodySlot" :class="textClasses">
          <slot name="body"></slot>
        </CxText>
      </div>
    </div>
    <div class="ml-auto d-flex align-items-center">
      <CxButton v-if="href" variant="link" :to="href" size="sm" class="alert-link mr-2">{{ linkText }}</CxButton>
      <CxButton v-if="closable" variant="close" icon="close" :class="`text-${props.variant}`" @click="onAlertClose" />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint no-unused-vars:off */
import { ALERT_VARIANTS } from "@/designSystem/Alert/constant";
import { computed, defineEmits, PropType, ref, useSlots } from "vue";
import { CxButton, CxIcon } from "@/designSystem";
import { CxTitle, CxText } from "@/designSystem/Typography";
import type { VariantType } from "@/designSystem/Alert/constant";

const props = defineProps({
  closable: {
    type: Boolean,
    default: true,
  },

  linkText: {
    type: String,
    required: false,
  },

  href: {
    type: String,
    required: false,
  },

  variant: {
    type: String as PropType<VariantType>,
    default: ALERT_VARIANTS.success,
    validator: (value: string): value is VariantType => value in ALERT_VARIANTS,
  },
});

const slots = useSlots();
const hasTitleSlot = !!slots.title;
const hasBodySlot = !!slots.body;

const alertClasses = computed(() => `flat-alert-${props.variant}`);
const textClasses = computed(() => `text-${props.variant}`);
const icon = computed(() => {
  switch (props.variant) {
    case ALERT_VARIANTS.success:
      return "new_releases";
    case ALERT_VARIANTS.danger:
      return "dangerous";
    case ALERT_VARIANTS.info:
      return "info";
    case ALERT_VARIANTS.warning:
      return "emergency_home";
    default:
      return "info";
  }
});

const isVisible = ref(true);

const emit = defineEmits<{
  (event: "onAlertClose"): void;
}>();

const onAlertClose = () => {
  isVisible.value = false;
  emit("onAlertClose");
};
</script>

<style lang="scss" scoped>
@import "@/designSystem/style/main.scss";
@import "@/designSystem/style/variables/_colors.scss";

@mixin flat-alert($color, $light) {
  background-color: $light;
  color: $color;
}

.flat-alert-success {
  @include flat-alert($primary, $primary-lighter);
}

.flat-alert-info {
  @include flat-alert($info, $info-lighter);
}

.flat-alert-danger {
  @include flat-alert($danger, $danger-lighter);
}

.flat-alert-warning {
  @include flat-alert($warning, $warning-lighter);
}
</style>
